/* eslint-disable react/prop-types */
/**
 * AddRecommendationButton
 * =======================
 * iter184 — Generic entry point for structured service recommendations.
 *
 * Currently active: Wheel Alignment.
 * Coming soon (gated by the catalog): ADAS Calibration, AC Service,
 * Diagnostic Testing, Brake Fluid, Geometry Check, Battery
 * Registration. Each renders disabled with a "Coming soon" badge so
 * FOH knows the surface is extensible.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PlusCircle, MoveRight } from "lucide-react";
import AlignmentRecommendationModal from "./AlignmentRecommendationModal";

export default function AddRecommendationButton({
  yestimateId,
  jobGroups = [],
  onSaved,
}) {
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    axios.get(`${API}/recommendations/catalog`)
      .then((r) => setCatalog(r.data?.recommendation_types || []))
      .catch(() => setCatalog([]));
  }, []);

  // iter185 — Edit-mode pre-fill. When an alignment recommendation
  // already exists on the yestimate, opening the modal lands in edit
  // mode with the existing values pre-selected. Prevents the silent
  // double-create the iter185 test report flagged.
  const existingAlignment = (jobGroups || []).find(
    (jg) => jg.subtype === "wheel_alignment"
            || jg.canonical_rule_id === "rule_wheel_alignment",
  );
  const initialForModal = existingAlignment
    ? {
        alignment_type:        existingAlignment.alignment_type,
        trigger_reasons:       existingAlignment.trigger_reasons || [],
        price_override:        existingAlignment.price_override,
        linked_repair_jg_ids:  existingAlignment.linked_repair_jg_ids || [],
        notes:                 existingAlignment.notes || "",
      }
    : null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className="text-[13px] h-9"
            data-testid="add-recommendation-btn"
          >
            <PlusCircle className="w-4 h-4 mr-1.5" />
            {existingAlignment ? "Edit Recommendation" : "Add Recommendation"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-slate-500">
            Structured recommendations
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {catalog.map((t) => {
            const isActive = t.status === "active";
            const isExisting = t.subtype === "wheel_alignment" && !!existingAlignment;
            const onPick = () => {
              if (!isActive) return;
              if (t.subtype === "wheel_alignment") setOpen(true);
            };
            return (
              <DropdownMenuItem
                key={t.subtype}
                disabled={!isActive}
                onClick={onPick}
                className="cursor-pointer flex items-start gap-2 py-2"
                data-testid={`add-recommendation-option-${t.subtype}`}
              >
                <MoveRight className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${
                  isActive ? "text-emerald-600" : "text-slate-300"
                }`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[13px] font-medium">
                      {isExisting ? `Edit ${t.label}` : t.label}
                    </span>
                    {!isActive && (
                      <span className="text-[9.5px] uppercase tracking-wide bg-slate-100 text-slate-500 border border-slate-200 px-1 py-0.5 rounded">
                        Soon
                      </span>
                    )}
                    {isExisting && (
                      <span className="text-[9.5px] uppercase tracking-wide bg-emerald-50 text-emerald-700 border border-emerald-200 px-1 py-0.5 rounded">
                        Added
                      </span>
                    )}
                  </div>
                  {t.description && (
                    <p className="text-[11px] text-slate-500 leading-snug mt-0.5">
                      {t.description}
                    </p>
                  )}
                </div>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlignmentRecommendationModal
        open={open}
        onOpenChange={setOpen}
        yestimateId={yestimateId}
        jobGroups={jobGroups}
        initial={initialForModal}
        onSaved={onSaved}
      />
    </>
  );
}
